import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const TIERS = {
  starter: {
    name: "Starter",
    monthlyPrice: 99,
    yearlyPrice: 79,
    priceId: "price_1ScumnDTyuOcf0PJnySR8VDY",
    description: "Perfect for small teams getting started",
    features: [
      "Up to 200 leads/month",
      "Basic AI lead scoring",
      "Email outreach sequences",
      "Standard analytics",
      "Email support",
    ],
    popular: false,
  },
  growth: {
    name: "Growth",
    monthlyPrice: 249,
    yearlyPrice: 199,
    priceId: "price_1ScumwDTyuOcf0PJaXxp7thH",
    description: "For growing teams that need more power",
    features: [
      "Unlimited leads",
      "Advanced AI call assist",
      "AI proposal generator",
      "Deal optimizer & forecasting",
      "Multi-channel outreach",
      "Priority support",
    ],
    popular: true,
  },
  enterprise: {
    name: "Enterprise",
    monthlyPrice: 2000,
    yearlyPrice: 1600,
    priceId: "price_1ScumxDTyuOcf0PJ16u6gTRj",
    description: "Custom solutions for large organizations",
    features: [
      "Everything in Growth",
      "Unlimited users",
      "Custom AI model tuning",
      "Dedicated success manager",
      "Custom integrations",
      "SLA guarantee",
      "On-premise option",
    ],
    popular: false,
  },
} as const;

const VALID_PRICE_IDS = Object.values(TIERS).map(tier => tier.priceId);

type PaymentProvider = "stripe" | "paypal";

export function PricingCards() {
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider>("stripe");
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async (tier: keyof typeof TIERS) => {
    if (!user) {
      toast.error("Please sign in to subscribe");
      navigate("/auth");
      return;
    }

    const tierData = TIERS[tier];
    const priceId = tierData.priceId;
    
    if (paymentProvider === "stripe") {
      if (!priceId || !VALID_PRICE_IDS.includes(priceId)) {
        toast.error("Invalid pricing tier");
        return;
      }
    }

    setLoading(tier);
    try {
      if (paymentProvider === "stripe") {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { priceId },
        });

        if (error) {
          throw new Error(error.message || "Failed to start checkout");
        }
        
        if (data?.url) {
          window.open(data.url, "_blank", "noopener,noreferrer");
        } else {
          throw new Error("No checkout URL returned");
        }
      } else {
        const { data, error } = await supabase.functions.invoke("paypal-create-subscription", {
          body: { tier },
        });

        if (error) {
          throw new Error(error.message || "Failed to start PayPal checkout");
        }
        
        if (data?.url) {
          window.open(data.url, "_blank", "noopener,noreferrer");
        } else {
          throw new Error("No PayPal checkout URL returned");
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create checkout session";
      toast.error(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <section id="pricing" className="py-14">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold mb-3">
            Simple, <span className="gradient-text">Transparent Pricing</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            Choose the plan that fits your team. All plans include a 14-day free trial.
          </p>

          <div className="flex items-center justify-center gap-2 mb-4">
            <span className={`text-sm ${!isYearly ? "text-foreground font-medium" : "text-muted-foreground"}`}>
              Monthly
            </span>
            <Switch checked={isYearly} onCheckedChange={setIsYearly} />
            <span className={`text-sm ${isYearly ? "text-foreground font-medium" : "text-muted-foreground"}`}>
              Yearly
              <span className="ml-1.5 text-xs text-primary font-semibold">Save 20%</span>
            </span>
          </div>

          {/* Payment Provider Toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">Pay with:</span>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50">
              <button
                onClick={() => setPaymentProvider("stripe")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  paymentProvider === "stripe"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <CreditCard className="h-3.5 w-3.5" />
                Stripe
              </button>
              <button
                onClick={() => setPaymentProvider("paypal")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  paymentProvider === "paypal"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.642h6.626c2.188 0 3.802.457 4.796 1.358 1.018.925 1.388 2.302 1.1 4.091-.043.267-.104.55-.186.858a9.347 9.347 0 0 1-.318 1.06c-.87 2.377-2.612 3.606-5.178 3.606H9.94a.95.95 0 0 0-.937.8l-.783 5.127a.643.643 0 0 1-.634.54h-.51v1.82h.001zM19.333 7.75c-.028.17-.06.337-.094.5-.869 3.723-3.312 4.87-6.589 4.87h-.853l-.808 5.124h-1.46l.81-5.123h.91c2.876 0 5.133-.939 5.892-3.893.22-.86.278-1.607.192-2.316v.838z"/>
                </svg>
                PayPal
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Object.entries(TIERS).map(([key, tier]) => (
            <div
              key={key}
              className={`glass rounded-xl p-5 relative hover-lift transition-all duration-300 ${
                tier.popular ? "border-primary ring-1 ring-primary/20" : ""
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                    <Sparkles className="h-3 w-3" />
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-5">
                <h3 className="text-lg font-bold mb-1">{tier.name}</h3>
                <p className="text-muted-foreground text-xs mb-3">{tier.description}</p>
                <div className="flex items-baseline justify-center gap-0.5">
                  <span className="text-3xl font-bold">
                    ${isYearly ? tier.yearlyPrice : tier.monthlyPrice}
                  </span>
                  <span className="text-muted-foreground text-sm">/user/mo</span>
                </div>
                {isYearly && (
                  <p className="text-xs text-primary mt-1">Billed annually</p>
                )}
              </div>

              <ul className="space-y-2 mb-5">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full ${tier.popular ? "ai-glow" : ""}`}
                variant={tier.popular ? "default" : "outline"}
                size="sm"
                onClick={() => handleSubscribe(key as keyof typeof TIERS)}
                disabled={loading === key}
              >
                {loading === key ? "Loading..." : `Pay with ${paymentProvider === "stripe" ? "Stripe" : "PayPal"}`}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
